import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  ProfileRoleSelector,
  ProfileUserFields,
  ProfileInfluencerFields,
  ProfileMerchantFields,
  ProfileDialogs,
} from '../components/profile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfileSetup() {
  const navigate = useNavigate();
  const [whatsapp, setWhatsapp] = useState('');
  const [role, setRole] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [commercialAddress, setCommercialAddress] = useState('');
  const [city, setCity] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [state, setState] = useState('');
  const [plan, setPlan] = useState('gratuito');
  const [merchantCategory, setMerchantCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [showWhatsAppGate, setShowWhatsAppGate] = useState(false);
  const [whatsAppSent, setWhatsAppSent] = useState(false);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [accessKey, setAccessKey] = useState('');
  const [activatingKey, setActivatingKey] = useState(false);
  const [influencerKey, setInfluencerKey] = useState('');
  const [influencerKeyValid, setInfluencerKeyValid] = useState(false);
  const [validatingInvite, setValidatingInvite] = useState(false);
  const [showInfluencerWhatsApp, setShowInfluencerWhatsApp] = useState(false);
  const [fullName, setFullName] = useState('');
  const [cpf, setCpf] = useState('');
  const [address, setAddress] = useState('');
  const [pix, setPix] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [cities, setCities] = useState([]);
  const [citySearch, setCitySearch] = useState('');
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const cityRef = useRef(null);

  useEffect(() => {
    if (!state) { setCities([]); setCity(''); setCitySearch(''); return; }
    axios.get(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${state}/municipios?orderBy=nome`)
      .then(res => setCities(res.data.map(c => c.nome)))
      .catch(() => setCities([]));
  }, [state]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cityRef.current && !cityRef.current.contains(e.target)) setShowCitySuggestions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCities = cities.filter(c =>
    c.toLowerCase().includes((citySearch || city || '').toLowerCase())
  ).slice(0, 8);

  const handleCitySelect = (c) => { setCity(c); setCitySearch(c); setShowCitySuggestions(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!role) { toast.error('Selecione o tipo de conta'); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      if (role === 'influencer' && !influencerKeyValid) {
        toast.error('Valide sua chave de convite antes de continuar');
        setLoading(false);
        return;
      }
      const payload = { role, city, state };
      if (role !== 'user') {
        payload.whatsapp = whatsapp;
        if (neighborhood) payload.neighborhood = neighborhood;
      }
      if (role === 'influencer') {
        payload.full_name = fullName; payload.cpf = cpf; payload.address = address; payload.pix = pix;
      }
      if (role === 'merchant') {
        if (!merchantCategory) { toast.error('Selecione a categoria do seu negócio'); setLoading(false); return; }
        payload.company_name = companyName; payload.cnpj = cnpj; payload.contact_name = contactName;
        payload.commercial_address = commercialAddress; payload.plan = plan; payload.merchant_category = merchantCategory;
      }
      await axios.put(`${BACKEND_URL}/api/auth/profile`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Perfil configurado com sucesso!');
      if (role === 'influencer') { navigate('/influencer'); }
      else if (role === 'merchant' && plan === 'gratuito') {
        try { await axios.post(`${BACKEND_URL}/api/subscription/activate-free`, {}, { headers: { Authorization: `Bearer ${token}` } }); }
        catch (err) { console.warn('Free plan activation skipped:', err.message); }
        setShowWhatsAppGate(true);
      } else if (role === 'merchant') { navigate('/merchant'); }
      else { navigate('/dashboard'); }
    } catch { toast.error('Erro ao atualizar perfil'); }
    finally { setLoading(false); }
  };

  const handleSendWhatsAppGreeting = () => {
    const msg = `Estou participando pelo plano Gratuito`;
    window.open(`https://wa.me/5517981124597?text=${encodeURIComponent(msg)}`, '_blank');
    setWhatsAppSent(true);
  };

  const handleValidateInfluencerInvite = async () => {
    if (!influencerKey.trim()) { toast.error('Digite a chave de convite'); return; }
    setValidatingInvite(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${BACKEND_URL}/api/validate-influencer-invite`, { key: influencerKey }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Chave validada! Voce e um influenciador.');
      setInfluencerKeyValid(true);
    } catch (error) { toast.error(error.response?.data?.detail || 'Chave invalida'); }
    finally { setValidatingInvite(false); }
  };

  const handleContractPlan = (planId) => {
    if (planId === 'gratuito') { setPlan(planId); return; }
    setPendingPlan(planId); setAccessKey(''); setShowKeyDialog(true);
  };

  const handleActivateKeySetup = async () => {
    if (!accessKey.trim()) { toast.error('Digite a chave de acesso'); return; }
    setActivatingKey(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${BACKEND_URL}/api/activate-plan`, { key: accessKey }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message); setPlan(res.data.plan); setShowKeyDialog(false);
    } catch (error) { toast.error(error.response?.data?.detail || 'Chave invalida'); }
    finally { setActivatingKey(false); }
  };

  const handleStripeCheckoutSetup = async () => {
    setActivatingKey(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${BACKEND_URL}/api/subscription/checkout`, {
        plan: pendingPlan, access_key: accessKey || '', origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.checkout_url) { window.location.href = res.data.checkout_url; }
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao iniciar pagamento'); }
    finally { setActivatingKey(false); }
  };

  const sharedCityProps = {
    state, setState, city, citySearch, setCitySearch, setCity,
    showCitySuggestions, setShowCitySuggestions, filteredCities, handleCitySelect, cityRef
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto mx-3 sm:mx-0">
        <h1 className="text-3xl font-black text-slate-900 mb-2" data-testid="setup-title">Complete seu Perfil</h1>
        <p className="text-slate-600 mb-6">Precisamos de algumas informacoes para continuar</p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {role && role !== 'user' && (
            <div>
              <Label htmlFor="whatsapp">Numero do WhatsApp</Label>
              <Input id="whatsapp" type="tel" placeholder="+5511999999999" value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)} required data-testid="whatsapp-input" className="mt-1" />
              <p className="text-xs text-slate-500 mt-1">Formato: +55 + DDD + numero</p>
            </div>
          )}

          <ProfileRoleSelector role={role} setRole={setRole} setShowInfluencerWhatsApp={setShowInfluencerWhatsApp} />
          {role === 'user' && <ProfileUserFields {...sharedCityProps} />}
          {role === 'influencer' && (
            <ProfileInfluencerFields
              influencerKey={influencerKey} setInfluencerKey={setInfluencerKey}
              influencerKeyValid={influencerKeyValid} validatingInvite={validatingInvite}
              onValidateInvite={handleValidateInfluencerInvite}
              fullName={fullName} setFullName={setFullName} cpf={cpf} setCpf={setCpf}
              neighborhood={neighborhood} setNeighborhood={setNeighborhood}
              address={address} setAddress={setAddress} pix={pix} setPix={setPix}
              {...sharedCityProps}
            />
          )}
          {role === 'merchant' && (
            <ProfileMerchantFields
              whatsapp={whatsapp} setWhatsapp={setWhatsapp}
              companyName={companyName} setCompanyName={setCompanyName}
              cnpj={cnpj} setCnpj={setCnpj}
              merchantCategory={merchantCategory} setMerchantCategory={setMerchantCategory}
              contactName={contactName} setContactName={setContactName}
              neighborhood={neighborhood} setNeighborhood={setNeighborhood}
              commercialAddress={commercialAddress} setCommercialAddress={setCommercialAddress}
              plan={plan} onContractPlan={handleContractPlan}
              {...sharedCityProps}
            />
          )}

          {role && (
            <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 rounded-lg"
              disabled={loading} data-testid="setup-submit-btn">
              {loading ? 'Salvando...' : 'Continuar'}
            </Button>
          )}
        </form>
      </div>

      <ProfileDialogs
        showKeyDialog={showKeyDialog} setShowKeyDialog={setShowKeyDialog}
        pendingPlan={pendingPlan} accessKey={accessKey} setAccessKey={setAccessKey}
        activatingKey={activatingKey} onActivateKey={handleActivateKeySetup} onStripeCheckout={handleStripeCheckoutSetup}
        showInfluencerWhatsApp={showInfluencerWhatsApp} setShowInfluencerWhatsApp={setShowInfluencerWhatsApp}
        influencerKeyValid={influencerKeyValid}
        showWhatsAppGate={showWhatsAppGate} onSendWhatsAppGreeting={handleSendWhatsAppGreeting}
        whatsAppSent={whatsAppSent} navigate={navigate}
      />
    </div>
  );
}
